import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import {
  MbModalComponent,
  MbInputComponent,
  MbSelectComponent,
  MbBannerComponent,
} from '../index';
import { ICaseDetail, IEditCasePayload } from '../../../core/services';
import { MbTextAreaComponent } from '../mb-text-area/mb-text-area.component';
import { ASSET_CONSTANTS } from '../../../shared/constants/asset-constants';
import { ESeverity } from '../../../utils/enums';

@Component({
  selector: 'app-mb-edit-case-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzModalModule,
    NzSelectModule,
    NzFormModule,
    NzButtonModule,
    NzDividerModule,
    MbModalComponent,
    MbInputComponent,
    MbSelectComponent,
    MbBannerComponent,
    MbTextAreaComponent,
  ],
  templateUrl: './mb-edit-case-modal.component.html',
})
export class MbEditCaseModalComponent implements OnChanges {
  @Input() isVisible = false;
  @Input() case: ICaseDetail | null = null;
  @Input() availableCategories: any[] = [];
  @Input() availableOrganizations: any[] = [];
  @Output() isVisibleChange = new EventEmitter<boolean>();
  @Output() submitEdit = new EventEmitter<IEditCasePayload>();
  @Output() cancelEdit = new EventEmitter<void>();
  editCaseForm: FormGroup;

  selectedInstitutions: string[] = [];
  availableTrackingLevels = ['DISTRICT', 'SECTOR', 'CELL', 'VILLAGE'];

  public isFormValid(): boolean {
    const editableControls = [
      'caseType',
      'severity',
      'trackingLevel',
      'estimatedDays',
      'primaryOrganizationId',
    ];

    return editableControls.every(
      (controlName) => this.editCaseForm.get(controlName)?.valid
    );
  }

  public onSubmit(): void {
    if (this.isFormValid()) {
      const formValue = this.editCaseForm.value;
      const editPayload: IEditCasePayload = {
        categoryIds: [formValue.caseType],
        organizationIds: formValue.institutions || [],
        primaryOrganizationId: formValue.primaryOrganizationId,
        severity: formValue.severity,
        trackingLevel: formValue.trackingLevel,
        estimatedResolutionDays: formValue.estimatedDays,
      };
      this.submitEdit.emit(editPayload);
    }
  }

  get caseTypeOptions$(): { value: string; label: string }[] {
    return this.availableCategories.map((cat) => ({
      value: cat.id,
      label: cat.name,
    }));
  }

  get trackingLevelOptions() {
    return this.availableTrackingLevels.map((l) => ({ value: l, label: l }));
  }

  get organizationOptions() {
    return this.availableOrganizations.map((org) => ({
      value: org.id,
      label: org.name,
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('ngOnChanges triggered:', changes);
    console.log('Current case:', this.case);
    console.log('Current isVisible:', this.isVisible);

    // When case data changes and modal is visible, populate the form
    if (changes['case'] && changes['case'].currentValue && this.isVisible) {
      console.log('Case changed and modal is visible, populating form');
      // Add a small delay to ensure form is ready
      setTimeout(() => this.populateEditForm(), 100);
    }

    // When modal becomes visible and we have case data, populate the form
    if (
      changes['isVisible'] &&
      changes['isVisible'].currentValue &&
      this.case
    ) {
      console.log('Modal became visible and case exists, populating form');
      setTimeout(() => this.populateEditForm(), 100);
    }
  }

  populateEditForm(): void {
    if (this.case) {
      console.log('Populating form with case data:', this.case);

      // Populate editable fields only
      this.editCaseForm.patchValue({
        caseType: this.case.categories?.[0]?.id || '',
        severity: this.case.caseSeverity || ESeverity.MEDIUM,
        trackingLevel: this.case.trackingLevel || 'MEDIUM',
        estimatedDays: this.case.estimatedResolutionDays || 0,
        primaryOrganizationId:
          this.case.primaryOrganization?.organizationId || '',
        institutions:
          this.case.organizations?.map((org: any) => org.organizationId) || [],
      });

      console.log('Form values after population:', this.editCaseForm.value);
      console.log('Case contact:', this.case.contact);
      console.log('Case location:', this.case.location);
      console.log('Case categories:', this.case.categories);

      this.selectedInstitutions =
        this.case.organizations?.map((org: any) => org.organizationId) || [];
    } else {
      console.log('No case data available for form population');
    }
  }

  handleCancel(): void {
    this.isVisible = false;
    this.isVisibleChange.emit(false);
    this.cancelEdit.emit();
  }

  handleSubmit(): void {
    this.onSubmit();
  }

  onInstitutionChange(selectedIds: string[]): void {
    this.selectedInstitutions = selectedIds || [];
    this.editCaseForm.patchValue({ institutions: this.selectedInstitutions });
  }

  removeInstitution(institutionId: string): void {
    this.selectedInstitutions = this.selectedInstitutions.filter(
      (id) => id !== institutionId
    );
    this.editCaseForm.patchValue({ institutions: this.selectedInstitutions });
  }

  getInstitutionName(institutionId: string): string {
    const institution = this.availableOrganizations.find(
      (org) => org.id === institutionId
    );
    return institution ? institution.name : 'Unknown';
  }

  constructor(private fb: FormBuilder) {
    this.editCaseForm = this.fb.group({
      // Editable fields (these will be updated via edit case endpoint)
      caseType: ['', Validators.required],
      severity: ['', Validators.required],
      trackingLevel: ['', Validators.required],
      estimatedDays: ['', [Validators.required, Validators.min(1)]],
      institutions: [[]],
      primaryOrganizationId: ['', Validators.required],
    });
  }

  protected readonly ASSET_CONSTANTS = ASSET_CONSTANTS;
}
